"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useGetDistrictDetailsQuery,
  useGetMeQuery,
} from "@/store/services";
import { useAutoTranslate } from "@/lib/i18n/useAutoTranslate";
import { isGagglePopupDisabled } from "@/utils/gaggle";

const GAGGLE_CHAT_URL =
  "https://d1ycp1unyf9l00.cloudfront.net/partner/708f607f-a9de-4d4d-8a0a-b2460192ef52/site/widget/index.html";

interface GaggleChatModalProps {
  open: boolean;
  onClose: () => void;
  setShouldText?: (value: boolean) => void;
}

const GaggleChatModal: React.FC<GaggleChatModalProps> = ({ setShouldText }) => {
  const tr = useAutoTranslate();
  const gagglePopupDisabled = isGagglePopupDisabled();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeActive, setIframeActive] = useState(false);
  const [coords, setCoords] = useState<{
    lat?: number;
    lon?: number;
    source?: "geolocation" | "ip";
  }>({});
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const { data: userProfile } = useGetMeQuery(undefined);
  const { data: districtDetails } = useGetDistrictDetailsQuery(
    userProfile?.schoolDistrictId ?? "",
    { skip: !userProfile?.schoolDistrictId },
  );
  const iframeHeight = "clamp(320px, 45vh, 480px)";
  const iframeMinHeight = "320px";

  const firstName = userProfile?.firstName?.trim();
  const lastName = userProfile?.lastName?.trim();
  const email = userProfile?.email?.trim();

  const hasCompleteIdentity = Boolean(firstName && lastName && email);
  const displayName = hasCompleteIdentity
    ? `${firstName} ${lastName} | ${email}`
    : "";

  const homeAddress = useMemo(() => {
    const parts = [
      typeof userProfile?.address === "string" ? userProfile.address.trim() : "",
      typeof userProfile?.city === "string" ? userProfile.city.trim() : "",
      typeof userProfile?.stateInitials === "string"
        ? userProfile.stateInitials.trim()
        : typeof userProfile?.state === "string"
          ? userProfile.state.trim()
          : "",
      typeof userProfile?.zipCode === "string" ? userProfile.zipCode.trim() : "",
    ].filter(Boolean);
    return parts.join(", ");
  }, [
    userProfile?.address,
    userProfile?.city,
    userProfile?.state,
    userProfile?.stateInitials,
    userProfile?.zipCode,
  ]);

  const organizationName =
    typeof districtDetails?.data?.name === "string"
      ? districtDetails.data.name.trim()
      : "";

  const schoolName = useMemo(() => {
    const schools = districtDetails?.data?.schools;
    if (!Array.isArray(schools) || !userProfile?.schoolId) return "";
    const match = schools.find(
      (school: { id: string; name: string }) =>
        school.id === userProfile.schoolId,
    );
    return match?.name?.trim?.() || "";
  }, [districtDetails?.data?.schools, userProfile?.schoolId]);

  const formatPhoneNumber = useCallback((value?: string | null) => {
    if (!value) return "";
    const digitsOnly = value.replace(/\D/g, "");
    if (!digitsOnly) return "";
    if (value.trim().startsWith("+")) return `+${digitsOnly}`;
    if (digitsOnly.length === 10) return `+1${digitsOnly}`;
    return `+${digitsOnly}`;
  }, []);

  if (gagglePopupDisabled) {
    return null;
  }

  useEffect(() => {
    let isCancelled = false;

    const updateCoordsIfEmpty = (
      next: { lat?: number; lon?: number; source: "geolocation" | "ip" },
    ) => {
      setCoords((prev) => {
        if (prev.lat != null && prev.lon != null) {
          // Prefer existing coordinates, especially if they already came from geolocation.
          if (prev.source === "geolocation" || next.source === prev.source) {
            return prev;
          }
        }
        return { ...prev, ...next };
      });
    };

    const fetchIpInfo = async () => {
      const setIpIfMissing = (value?: string | null) => {
        if (!value) return false;
        const trimmed = value.trim();
        if (!trimmed) return false;
        setIpAddress((prev) => prev ?? trimmed);
        return true;
      };

      const sources: Array<() => Promise<string | null | undefined>> = [
        async () => {
          const response = await fetch("https://ipapi.co/json/");
          if (!response.ok) return null;
          const data = await response.json();
          if (data?.latitude && data?.longitude) {
            updateCoordsIfEmpty({
              lat: data.latitude,
              lon: data.longitude,
              source: "ip",
            });
          }
          return data?.ip;
        },
        async () => {
          const response = await fetch("https://api.ipify.org?format=json");
          if (!response.ok) return null;
          const data = await response.json();
          return data?.ip;
        },
        async () => {
          const response = await fetch("https://ifconfig.me/ip");
          if (!response.ok) return null;
          const text = await response.text();
          return text;
        },
      ];

      let foundIp = false;
      for (const getIp of sources) {
        if (isCancelled || foundIp) break;
        try {
          const ip = await getIp();
          if (isCancelled) break;
          if (setIpIfMissing(ip)) {
            foundIp = true;
          }
        } catch (error) {
          console.error("Failed to fetch IP info", error);
        }
      }

      if (!isCancelled && !foundIp) {
        setIpAddress((prev) => prev ?? "unknown");
      }
    };

    const requestGeoLocation = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (isCancelled) return;
          updateCoordsIfEmpty({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: "geolocation",
          });
        },
        (error) => {
          console.warn("Geolocation unavailable, will fall back to IP", error);
        },
        { timeout: 5000, maximumAge: 60000 },
      );
    };

    requestGeoLocation();
    fetchIpInfo();

    return () => {
      isCancelled = true;
    };
  }, []);

  const contactParams = useMemo(() => {
    if (!hasCompleteIdentity || !ipAddress) return null;

    return {
      displayName,
      contactName: `${firstName ?? ""} ${lastName ?? ""}`.trim(),
      email: email || "",
      homeAddress,
      school: schoolName,
      organization: organizationName,
      caregiverName: userProfile?.careGiverName?.trim() || "",
      caregiverNumber: formatPhoneNumber(userProfile?.careGiverNumber || ""),
      caregiverEmail: userProfile?.careGiverEmail?.trim() || "",
      emergencyContactName: userProfile?.emergencyContactName?.trim() || "",
      emergencyContactNumber: formatPhoneNumber(
        userProfile?.emergencyContactNumber || "",
      ),
      ipAddress,
      latitude:
        coords.lat != null && !Number.isNaN(coords.lat)
          ? `${coords.lat}`
          : "",
      longitude:
        coords.lon != null && !Number.isNaN(coords.lon)
          ? `${coords.lon}`
          : "",
    };
  }, [
    coords.lat,
    coords.lon,
    displayName,
    email,
    firstName,
    formatPhoneNumber,
    hasCompleteIdentity,
    homeAddress,
    ipAddress,
    lastName,
    organizationName,
    schoolName,
    userProfile?.careGiverEmail,
    userProfile?.careGiverName,
    userProfile?.careGiverNumber,
    userProfile?.emergencyContactName,
    userProfile?.emergencyContactNumber,
  ]);

  const iframeSrc = useMemo(() => {
    if (!contactParams) return null;
    try {
      const url = new URL(GAGGLE_CHAT_URL);
      url.searchParams.set("displayName", contactParams.displayName);
      Object.entries(contactParams).forEach(([key, value]) => {
        if (value == null || key === "displayName") return;
        url.searchParams.set(key, value);
      });
      return url.toString();
    } catch (error) {
      console.error("Failed to build chat URL", error);
      return null;
    }
  }, [contactParams]);

  useEffect(() => {
    const handleWindowBlur = () => {
      if (document.activeElement === iframeRef.current) {
        setIframeActive(true);
        setTimeout(() => {
          setShouldText?.(true);
        }, 30000);
      }
    };
    const handleWindowFocus = () => {
      if (iframeActive) {
        setIframeActive(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log(`:keyboard: Key pressed: ${e.key}`);
    };
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [iframeActive, setShouldText]);

  return (
    <>
      {iframeSrc ? (
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          width="100%"
          height="100%"
          style={{
            border: "none",
            maxWidth: "100%",
            minHeight: iframeMinHeight,
            height: iframeHeight,
          }}
        />
      ) : (
        <div
          className="flex items-center justify-center rounded-2xl border border-dashed border-rose-100 bg-white text-sm text-gray-500"
          style={{ minHeight: iframeMinHeight, height: iframeHeight }}
        >
          {tr("Connecting you to a counselor…")}
        </div>
      )}
    </>
  );
};

export default GaggleChatModal;
